// src/home/components/CategoryBar.tsx
// Horizontal scrollable category chips — YouTube/Rumble style
import React, { useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface Category {
  id: string;
  label: string;
}

export const DASHBOARD_CATEGORIES: Category[] = [
  { id: 'all',       label: 'All' },
  { id: 'gaming',    label: 'Gaming' },
  { id: 'sports',    label: 'Sports' },
  { id: 'music',     label: 'Music' },
  { id: 'tech',      label: 'Tech' },
  { id: 'finance',   label: 'Finance' },
  { id: 'news',      label: 'News' },
  { id: 'fitness',   label: 'Fitness' },
  { id: 'comedy',    label: 'Comedy' },
  { id: 'education', label: 'Education' },
  { id: 'cars',      label: 'Cars' },
  { id: 'food',      label: 'Food' },
  { id: 'travel',    label: 'Travel' },
  { id: 'live',      label: '🔴 Live' },
  { id: 'shorts',    label: 'Shorts' },
];

/** Keyword sets used to classify videos by title/channelName */
export const CATEGORY_KEYWORDS: Record<string, string[]> = {
  gaming:    ['game', 'gaming', 'gameplay', 'gamer', 'esport', 'minecraft', 'fortnite', 'warzone', 'call of duty', 'playstation', 'xbox', 'nintendo', 'steam', 'fps', 'rpg', 'mmorpg', 'street fighter', 'mortal kombat', 'valorant', 'apex'],
  sports:    ['nba', 'nfl', 'nhl', 'mlb', 'soccer', 'football', 'basketball', 'baseball', 'tennis', 'mma', 'ufc', 'boxing', 'sport', 'match', 'tournament', 'goal', 'highlights', 'season', 'playoff', 'championship', 'league', 'tst', 'squad'],
  music:     ['music', 'song', 'album', 'rap', 'hip hop', 'pop', 'jazz', 'concert', 'artist', 'remix', 'track', 'beat', 'mixtape', 'r&b', 'playlist'],
  tech:      ['tech', 'technology', 'ai', 'artificial intelligence', 'programming', 'coding', 'software', 'hardware', 'iphone', 'android', 'computer', 'gpu', 'cpu', 'machine learning', 'llm', 'claude', 'chatgpt', 'gpt', 'robot', 'drone'],
  finance:   ['finance', 'invest', 'crypto', 'bitcoin', 'stock', 'money', 'trading', 'wealth', 'economy', 'market', 'dollar', 'income', 'real estate', 'side hustle', 'passive income', 'entrepreneur', 'startup', 'business'],
  news:      ['news', 'breaking', 'politics', 'government', 'election', 'war', 'update', 'world', 'report', 'president', 'congress', 'senate', 'policy', 'ukraine', 'israel', 'china'],
  fitness:   ['fitness', 'workout', 'gym', 'weight', 'health', 'diet', 'exercise', 'muscle', 'training', 'cardio', 'yoga', 'crossfit', 'bodybuilding', 'lose weight', 'nutrition'],
  comedy:    ['comedy', 'funny', 'laugh', 'prank', 'joke', 'meme', 'roast', 'skit', 'standup', 'parody', 'wtf', 'lol', 'hilarious'],
  education: ['learn', 'tutorial', 'how to', 'tips', 'guide', 'explained', 'education', 'lesson', 'course', 'study', 'science', 'history', 'documentary', 'analysis'],
  cars:      ['car', 'auto', 'vehicle', 'drive', 'supercar', 'ferrari', 'lamborghini', 'bmw', 'mercedes', 'tesla', 'track', 'race', 'drag', 'motor', 'horsepower', 'automotive', 'drift'],
  food:      ['food', 'cook', 'recipe', 'restaurant', 'eat', 'chef', 'kitchen', 'meal', 'dinner', 'breakfast', 'lunch', 'bake', 'grill', 'taste'],
  travel:    ['travel', 'vlog', 'trip', 'vacation', 'explore', 'adventure', 'country', 'city', 'tour', 'backpack', 'flight', 'hotel'],
};

interface Props {
  active: string;
  onChange: (id: string) => void;
}

export default function CategoryBar({ active, onChange }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'right' ? 240 : -240, behavior: 'smooth' });
  };

  return (
    <div className="relative flex items-center w-full">
      {/* Left arrow */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-zinc-900/90 border border-zinc-700 shadow-lg hover:bg-zinc-800 transition-colors shrink-0"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-4 h-4 text-white" />
        </button>
      )}

      {/* Chips */}
      <div
        ref={scrollRef}
        onScroll={updateArrows}
        className="flex gap-2 overflow-x-auto scrollbar-hide px-1 py-1 w-full"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {DASHBOARD_CATEGORIES.map((cat) => {
          const isActive = active === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => onChange(cat.id)}
              className={`
                shrink-0 rounded-lg px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap
                ${isActive
                  ? 'bg-white text-black'
                  : 'bg-zinc-800 text-zinc-200 hover:bg-zinc-700'}
              `}
            >
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Right arrow */}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-zinc-900/90 border border-zinc-700 shadow-lg hover:bg-zinc-800 transition-colors shrink-0"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-4 h-4 text-white" />
        </button>
      )}
    </div>
  );
}
